import React, { useContext, useEffect, useMemo, useState } from "react";
import { Form, Input, Select, Button, Modal } from "antd";
import { AuthContext } from "context/AuthContext";
import { SvgBack } from "ui/svgs";
import { createDealer, editDealerEmail, resendActivateEmail } from "./service";
import { showToast } from "ui/components/common";
import { User } from "context/AuthContext";
import momentTimezone from "moment-timezone";
import PubSub from "pubsub-js";
const { Option } = Select;

export function CreateDealerPage(props: {
  curDealer: User;
  onBack: () => void;
}) {
  const { user } = useContext(AuthContext);
  const [submitting, setSubmitting] = useState(false);
  const { onBack, curDealer } = props;

  const onFinish = async (formData: any) => {
    console.log("Form values:", formData);
    setSubmitting(true);
    const res = await createDealer(
      user?.orgId,
      formData?.dealerEmail,
      formData?.timeZone,
      curDealer?.organization?.timezone,
      formData?.source,
    );
    setSubmitting(false);
    if (res.status !== 200) {
      showToast(1, "Failed to create dealer. Please try again later.");
      return;
    }
    showToast(0, "Create dealer success", { minWidth: 300 });
    onBack?.();
    PubSub.publish("dealerCreated");
  };
  const filterTimeZones = useMemo(() => {
    const timeZonesOption = momentTimezone.tz.names().map((timeZone) => {
      const offset = momentTimezone.tz(timeZone).format("Z");
      return {
        value: timeZone,
        label: timeZone + "(" + offset + ")",
      };
    });

    const slashIndex = momentTimezone.tz.guess().indexOf("/");
    const currentState = momentTimezone.tz.guess().slice(0, slashIndex);
    const filterTimeZones = timeZonesOption.filter((timeZone) => {
      return timeZone.label.includes(currentState);
    });
    return filterTimeZones;
  }, []);

  return (
    <div className="flex flex-col items-center h-full p-6">
      <div className="h-12 w-[600px] flex items-center gap-10 mb-8 text-xl font-semibold">
        <button
          className="w-12 h-12 flex items-center justify-center"
          onClick={onBack}
        >
          <SvgBack width="16" className="fill-zinc-500" />
        </button>
        Create Dealer
      </div>
      <Form
        name="create_dealer"
        layout="vertical"
        onFinish={onFinish}
        className="md:w-[600px]"
        initialValues={{
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          source: "source_alpha",
        }}
      >
        <Form.Item
          label="Dealer Email"
          name="dealerEmail"
          rules={[
            { required: true, message: "Please input the dealer email!" },
          ]}
        >
          <Input className="h-[54px] rounded-2xl" />
        </Form.Item>
        <Form.Item
          label="Time Zone"
          name="timeZone"
          rules={[{ required: true, message: "Please select a time zone!" }]}
        >
          <Select
            showSearch
            placeholder="Select a time zone"
            className="h-[54px] rounded-2xl"
            defaultValue={momentTimezone.tz.guess()}
          >
            {filterTimeZones?.map((timeZone) => (
              <Option value={timeZone.value} key={timeZone.value}>
                {timeZone.label}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Site"
          name="source"
          rules={[{ required: true, message: "Please select a site!" }]}
        >
          <Select
            allowClear
            placeholder="Select a site"
            className="h-[54px] rounded-2xl"
            options={[
              { label: "Alpha Vision", value: "source_alpha" },
              { label: "A Plus Guard", value: "source_guard" },
            ]}
          />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            disabled={submitting}
            className="float-right flex justify-center h-[54px] w-[100px] items-center rounded-2xl bg-blue-500 px-6 text-base font-medium text-white transition-colors hover:bg-blue-400 disabled:bg-blue-300 disabled:hover:bg-blue-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
          >
            Create
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}

export function UpdateDealerPage(props: {
  curDealer: User;
  onBack: () => void;
}) {
  const { onBack, curDealer } = props;

  const [form] = Form.useForm();

  useEffect(() => {
    form.resetFields();
  }, [curDealer]);
  const onResendEmail = async () => {
    const res = await resendActivateEmail(curDealer.id);
    if (res.status === 200) {
      showToast(0, "Resend email success");
    } else {
      showToast(1, "Failed to resend email");
    }
  };
  const onFinish = async (formData: any) => {
    console.log("Form values:", formData);
    const res = await editDealerEmail(curDealer.id, formData.dealerEmail);
    if (res.status === 200) {
      showToast(0, "Edit dealer success");
    } else {
      showToast(1, "Failed to edit dealer");
    }
  };

  function onDelete() {
    Modal.confirm({
      title: <p className="font-semibold text-xl pb-3">Confirm Delete </p>,
      // type: "warn",
      icon: null,
      centered: true,
      content: "Are you sure you want to delete this dealer?",
      async onOk() {
        // const res = await deleteAudio(audio.id);
        // if (res.status === 200) {
        //   showToast(0, "Delete dealer success");
        //   //  onRefresh?.();
        // } else {
        //   showToast(1, "Failed to delete dealer");
        // }
      },
      okText: "Delete",
      okButtonProps: {
        className:
          "bg-red-500 !border-none text-white px-6 py-[26px] rounded-2xl",
      },
      cancelButtonProps: {
        className: "primaryButton bordered",
      },
    });
  }

  const timeZonesOption = useMemo(() => {
    const timeZonesOption = momentTimezone.tz.names().map((timeZone) => {
      const offset = momentTimezone.tz(timeZone).format("Z");
      return {
        value: timeZone,
        label: timeZone + "(" + offset + ")",
      };
    });
    return timeZonesOption;
  }, []);

  return (
    <div className="flex flex-col items-center h-full p-6">
      <div className="h-12 w-[600px] flex items-center gap-10 mb-8 text-xl font-semibold">
        <button
          className="w-12 h-12 flex items-center justify-center"
          onClick={onBack}
        >
          <SvgBack width="16" className="fill-zinc-500" />
        </button>
        Edit Dealer
      </div>
      <Form
        form={form}
        name="create_dealer"
        layout="vertical"
        onFinish={onFinish}
        className="md:w-[600px]"
        initialValues={{
          dealerEmail: curDealer?.email,
          timeZone: curDealer?.timezone,
          source: curDealer?.source,
        }}
      >
        <Form.Item
          label="Dealer Email"
          name="dealerEmail"
          rules={[
            { required: true, message: "Please input the dealer email!" },
          ]}
        >
          <Input className="h-[54px] rounded-2xl" />
        </Form.Item>
        <Form.Item
          label="Time Zone"
          name="timeZone"
          rules={[{ required: true, message: "Please select a time zone!" }]}
        >
          <Select
            placeholder="Select a time zone"
            className="h-[54px] rounded-2xl"
            disabled
          >
            {timeZonesOption?.map((timeZone) => (
              <Option value={timeZone.value}>{timeZone.label}</Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Site"
          name="source"
          rules={[{ required: true, message: "Please select a site!" }]}
        >
          <Select
            disabled
            allowClear
            placeholder="Select a site"
            className="h-[54px] rounded-2xl"
            options={[
              { label: "Alpha Vision", value: "source_alpha" },
              { label: "A Plus Guard", value: "source_guard" },
            ]}
          />
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            //disabled={true}
            onSubmit={onFinish}
            className="float-right flex justify-center h-[54px] w-[100px] items-center rounded-2xl bg-blue-500 px-6 text-base font-medium text-white "
          >
            Save
          </Button>
          <div
            className="cursor-not-allowed  float-right font-medium text-base border-none flex justify-center h-[54px] w-[100px] items-center text-[#F73D2F] hover:text-[#f8827a]"
            //onClick={onDelete}
          >
            Delete
          </div>
        </Form.Item>
        <Form.Item>
          <Button
            type="primary"
            //disabled={true}
            onClick={onResendEmail}
            className="float-right flex justify-center h-[54px] w-[200px] items-center rounded-2xl bg-blue-500 px-6 text-base font-medium text-white "
          >
            Resend Avtivate Email
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}
